import React, { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  ListBulletIcon,
  Squares2X2Icon,
  ClockIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  CubeIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import { Button, PageHero } from '../ui';
import DealsList from './DealsList';
import PipelineBoard from './PipelineBoard';
import ActivityLogView from './ActivityLogView';
import StockAlertsView from './StockAlertsView';
import PickingPackingPage from './PickingPackingPage';

type TabType = 'list' | 'board' | 'activity' | 'alerts' | 'picking';

interface TabConfig {
  id: TabType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const tabs: TabConfig[] = [
  { id: 'list', label: 'Orders List', icon: ListBulletIcon },
  { id: 'board', label: 'Pipeline Board', icon: Squares2X2Icon },
  { id: 'picking', label: 'Picking & Packing', icon: CubeIcon },
  { id: 'activity', label: 'Activity Log', icon: ClockIcon },
  { id: 'alerts', label: 'Stock Alerts', icon: ExclamationTriangleIcon },
];

const DealsPage: React.FC = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<TabType>('list');

  // Check if we're in admin context
  const isAdminContext = location.pathname.startsWith('/admin');
  const basePath = isAdminContext ? '/admin' : '/app';

  const renderTabContent = () => {
    switch (activeTab) {
      case 'list':
        return <DealsList embedded />;
      case 'board':
        return <PipelineBoard embedded />;
      case 'picking':
        return <PickingPackingPage />;
      case 'activity':
        return <ActivityLogView />;
      case 'alerts':
        return <StockAlertsView />;
      default:
        return <DealsList embedded />;
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 space-y-6" data-tour="page-content">
      {/* Enhanced Hero Section */}
      <PageHero
        title="Order Management"
        subtitle="Manage orders, track fulfillment, and monitor inventory alerts"
        icon={<CurrencyDollarIcon className="w-5 h-5 lg:w-6 lg:h-6 text-white drop-shadow-lg" />}
        accentColor="green"
      >
        <div className="mt-2">
          <Link to={`${basePath}/deals/new`}>
            <Button variant="primary" leftIcon={<PlusIcon className="h-5 w-5" />}>
              New Order
            </Button>
          </Link>
        </div>
      </PageHero>

      {/* Tabs */}
      <div className="border-b border-neutral-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
                  ${isActive
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
                  }
                `}
              >
                <Icon
                  className={`
                    -ml-0.5 mr-2 h-5 w-5
                    ${isActive ? 'text-primary-500' : 'text-neutral-400 group-hover:text-neutral-500'}
                  `}
                />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default DealsPage;

